import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from './ui'

/* Step-by-step wizard shell for the PWA's create/log forms.
 *
 * Mobile (≤1023px): one step at a time - numbered progress pills, a step
 * title, Back/Next controls, and the host form's own submit row shown only
 * on the final step. Desktop: renders every step's content stacked in
 * order with the footer at the end, which is byte-for-byte the layout
 * these forms already had - so wrapping an existing form in <Wizard> does
 * not change the web UI at all.
 *
 * This is deliberately layout-only: it lives INSIDE the host's <form> and
 * never owns submission. `footer` is the form's existing submit row, so
 * validation, mutation state and error display all stay exactly where
 * they were. Next is type="button" (never submits); native `required`
 * fields on hidden steps don't block submission because hidden inputs are
 * skipped by constraint validation (display:none), same technique the
 * admin UserEdit wizard already relies on.
 */
export interface WizardStep {
  title: string
  content: ReactNode
}

export function Wizard({ steps, footer }: { steps: WizardStep[]; footer: ReactNode }) {
  const [step, setStep] = useState(0)
  const last = steps.length - 1
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  /* A `required` field on a CSS-hidden step silently BLOCKS form submission
   * in Chrome ("not focusable" - no visible error), so Next validates the
   * current step's own fields before advancing: by the time the submit row
   * is reachable on the last step, every earlier step has already passed.
   * Dots only jump backwards for the same reason. */
  function next() {
    const el = stepRefs.current[step]
    if (el) {
      for (const input of el.querySelectorAll<HTMLInputElement>('input, select, textarea')) {
        if (!input.checkValidity()) {
          input.reportValidity()
          return
        }
      }
    }
    setStep((s) => Math.min(s + 1, last))
  }

  return (
    <>
      {/* Progress header - shown at every size now that desktop steps too. */}
      <div className="wiz__head">
        <div className="wiz__dots">
          {steps.map((s, i) => (
            <button
              key={s.title} type="button"
              className={`wiz__dot${i === step ? ' wiz__dot--active' : ''}${i < step ? ' wiz__dot--done' : ''}`}
              onClick={() => { if (i <= step) setStep(i) }}
              aria-label={`Step ${i + 1}: ${s.title}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <div className="wiz__title">
          Step {step + 1} of {steps.length} — {steps[step].title}
        </div>
      </div>

      {/* Steps stay mounted (CSS-hidden) so field state and file inputs
          survive navigation between steps. */}
      {steps.map((s, i) => (
        <div
          key={s.title}
          ref={(el) => { stepRefs.current[i] = el }}
          className={`wiz__step${i === step ? ' wiz__step--current' : ''}`}
        >
          {s.content}
        </div>
      ))}

      {/* Back/Next, at every size. This was mobile-only while desktop
          stacked every step; now that desktop steps too, hiding the nav
          left the form advanceable only on a phone. */}
      <div className="wiz__nav">
        {step > 0 && (
          <Button type="button" variant="secondary" onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
        )}
        {step < last && (
          <Button type="button" onClick={next} style={{ marginLeft: 'auto' }}>
            Next
          </Button>
        )}
      </div>
      <div className={step === last ? 'wiz__footer wiz__footer--show' : 'wiz__footer'}>
        {footer}
      </div>
    </>
  )
}
